import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Platform } from '@app/classes';
import { BulkSelectInterviewersDialogComponent } from '@app/components';
import { Applicant } from '@app/models/recruiting/applicant.model';
import { Interview } from '@app/models/recruiting/interview.model';
import { JobPosting } from '@app/models/recruiting/job-posting.model';
import { ManageInterviewService } from '@app/modules/applicant-tracker/services';
import { JobPostingApplicantInterviewsTable } from '@app/modules/applicant-tracker/services/tables/job-posting-applicant-interviews-table/job-posting-applicant-interviews-table';
import { IndexView } from '@app/modules/common/views';
import { SettingsIntegrationService } from '@app/modules/settings/services';
import { AnalyticService, AuthService, NotifyService } from '@app/services';

@Component({
    templateUrl: './job-posting-interviews-index.template.html',
    styleUrls: ['./job-posting-interviews-index.style.scss'],
})
export class JobPostingInterviewsIndexView extends IndexView implements OnInit {
    @ViewChild('interviewerSelect', { static: true }) interviewerSelect: BulkSelectInterviewersDialogComponent;
    jobPosting: JobPosting;
    openCreateInterviewDialog = false;
    interviews: Interview[] = [];
    isLoading = false;
    canCreateInterviews = false;
    applicantInterviewTables: JobPostingApplicantInterviewsTable[] = [];
    selectedInterview: Interview;

    constructor(
        public route: ActivatedRoute,
        private router: Router,
        private integrationService: SettingsIntegrationService,
        private notify: NotifyService,
        private auth: AuthService,
        private manageInterviewService: ManageInterviewService,
        private analyticService: AnalyticService
    ) {
        super();
        this.jobPosting = this.route.snapshot.data.models.get('jobPosting');
    }

    async ngOnInit(): Promise<void> {
        this.isLoading = true;

        this.canCreateInterviews = this.auth.can(Platform.recruiting.permission.recruiter);

        const [interviews] = await Interview.param('company', this.auth.company.id)
            .where('atsJobId', this.jobPosting.id)
            .all();

        this.interviews = interviews;

        const applicantInterviewTables: JobPostingApplicantInterviewsTable[] = this.interviews.map((interview) => {
            return new JobPostingApplicantInterviewsTable(interview, this.analyticService);
        });

        this.applicantInterviewTables = applicantInterviewTables;

        this.isLoading = false;
    }

    async onPause(pause: boolean): Promise<void> {
        try {
            this.isLoading = true;
            this.jobPosting = await this.integrationService.pauseJobIntegration(this.jobPosting.id, pause);
            this.notify.success({
                key: 'applicant-tracker.applicants.jobPostingHasBeenActionSuccessfully',
                params: { action: pause ? 'paused' : 'unpaused' },
            });
        } catch (err) {
            this.notify.error(err);
        }

        this.isLoading = false;
    }

    showCreateInterviewDialog(): void {
        this.openCreateInterviewDialog = true;
    }

    createApplicantInterview(existingInterview: Interview | null): void {
        if (!existingInterview) {
            this.router.navigate(['/recruiting', 'jobPostings', this.jobPosting.id, 'interviews', 'create']);
            return;
        }

        this.router.navigate([
            '/recruiting',
            'jobPostings',
            this.jobPosting.id,
            'interviews',
            'existing',
            existingInterview.id,
        ]);
    }

    closeApplicantCreateInterviewDialog(): void {
        this.openCreateInterviewDialog = false;
    }

    goToInterviewDetails(interview: Interview): void {
        this.router.navigate(['/recruiting', 'jobPostings', this.jobPosting.id, 'interviews', interview.id]);
    }

    async onInterviewAssign(interview: Interview): Promise<void> {
        this.selectedInterview = interview;
        this.interviewerSelect.interview = interview;

        this.interviewerSelect.show().then((selectedApplicants: Applicant[]) => {
            if (!selectedApplicants) {
                return;
            }

            this.router.navigate(['./manage'], { relativeTo: this.route });

            this.manageInterviewService.selectedApplicants = selectedApplicants;
            this.manageInterviewService.jobPosting = this.jobPosting;
            this.manageInterviewService.interview = interview;
        });
    }
}
